/**
 * Ordering Content Type — Editor
 * Tabs: Content (title, description, Page) · Questions (sidebar + ordering) · Configuration
 */

import * as React from 'react';
import { Input, Label, Textarea, cn, createEmptyDocument } from '@ai-content-hub/content-kit';
import {
    type EditTabDescriptor,
    type CompositeQuestionsSidebarRow,
    generateId,
    CompositeEditShell,
    CompositeEditContentTab,
    CompositeEditSortableQuestionsLayout,
    orderItemsByQuestionsSidebarRows,
    persistQuestionsSidebarRows,
    reconcileQuestionsSidebarRows,
    resolvedQuestionsSidebarRows,
    pickErrorsUnderPrefix,
    exerciseDataErrorsPrefix,
    hasErrorsForExerciseIndex,
    useLocale,
} from '@ai-content-hub/content-core';
import {
    OrderingEdit as OrderingElementEdit,
    PageEdit,
    type OrderingElementData,
    type PageElementData,
    normalizeOrderingData,
} from '@ai-content-hub/content-elements';
import type { BlockContentType } from '@ai-content-hub/content-elements';
import type { OrderingContentData, OrderingContentConfig, OrderingContentExercise } from './schema';
import {
    defaultOrderingContentData,
    normalizeOrderingContentData,
    serializableOrderingContentData,
} from './schema';
import { FileText, ListOrdered, Settings } from 'lucide-react';
import map from 'lodash/map';

const PAGE_BLOCK_TYPES: BlockContentType[] = [
    'rich-text',
    'image',
    'audio',
    'office-document',
    'text',
];

const ORDERING_EXERCISE_TYPE_ID = 'ordering-exercise';

/** Editor display options — matches EditorDisplayOptions from content-core */
interface EditorDisplayOptions {
    showTitle?: boolean;
    showDescription?: boolean;
}

type TabValue = 'content' | 'questions' | 'config';

function exercisePreview(
    ex: OrderingContentExercise,
    index: number,
    t: (key: string, defaultMessage?: string) => string
): string {
    const q = ex.data?.question?.trim();
    const n = ex.data?.items?.length ?? 0;
    if (q) {
        return q.length > 48 ? `${q.slice(0, 48)}…` : q;
    }
    return t('exercisePreview', 'Question {{index}} — {{n}} items')
        .replace('{{index}}', String(index + 1))
        .replace('{{n}}', String(n));
}

export interface OrderingContentEditProps {
    value: OrderingContentData;
    onChange: (value: OrderingContentData) => void;
    config?: OrderingContentConfig;
    onConfigChange?: (config: OrderingContentConfig) => void;
    disabled?: boolean;
    editorOptions?: EditorDisplayOptions;
    configSlot?: React.ReactNode;
    className?: string;
    errors?: Record<string, string>;
}

export const OrderingContentEdit: React.FC<OrderingContentEditProps> = ({
    value: data,
    onChange,
    config,
    onConfigChange: _onConfigChange,
    disabled = false,
    editorOptions,
    configSlot,
    className,
    errors,
}) => {
    const { t } = useLocale({ config: config?.i18n });
    const [activeTab, setActiveTab] = React.useState<TabValue>('content');

    const safeData = normalizeOrderingContentData(data ?? defaultOrderingContentData);
    const exercises = safeData.exercises;

    const updateData = (updates: Partial<OrderingContentData>) => {
        onChange(
            serializableOrderingContentData(
                normalizeOrderingContentData({ ...safeData, ...updates })
            )
        );
    };

    const contentSectionCount = safeData.lessonContent?.sections?.length ?? 0;

    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [selectedSectionId, setSelectedSectionId] = React.useState<string | null>(null);

    // EAA-2083: only the exercise selection follows `exercises`; the section selection is left
    // alone. Every sidebar-rows change (typing a section title included) rebuilds `exercises`
    // as a new array, so with no exercises yet this effect cleared the selected section on
    // the first keystroke and closed its panel. A section that is really removed is
    // deselected by CompositeEditSortableQuestionsLayout itself.
    React.useEffect(() => {
        if (exercises.length === 0) {
            setSelectedId(null);
            return;
        }
        setSelectedId((prev) => {
            if (prev && exercises.some((i) => i.id === prev)) {
                return prev;
            }
            return exercises[0].id;
        });
    }, [exercises]);

    const handleLessonChange = (lessonContent: PageElementData) => {
        updateData({ lessonContent });
    };

    const addExercise = () => {
        if (disabled) {
            return;
        }
        const next: OrderingContentExercise = {
            id: generateId(),
            data: normalizeOrderingData({}),
        };
        const nextEx = [...exercises, next];
        updateData({
            exercises: nextEx,
            questionsSidebarRows: persistQuestionsSidebarRows(
                reconcileQuestionsSidebarRows(safeData.questionsSidebarRows, map(nextEx, (e) => e.id))
            ),
        });
        setSelectedId(next.id);
        setSelectedSectionId(null);
        setActiveTab('questions');
    };

    const removeExercise = (itemId: string) => {
        if (disabled || exercises.length <= 1) {
            return;
        }
        const nextEx = exercises.filter((i) => i.id !== itemId);
        updateData({
            exercises: nextEx,
            questionsSidebarRows: persistQuestionsSidebarRows(
                reconcileQuestionsSidebarRows(safeData.questionsSidebarRows, map(nextEx, (e) => e.id))
            ),
        });
    };

    const updateExercise = (itemId: string, next: OrderingContentExercise) => {
        if (disabled) {
            return;
        }
        updateData({
            exercises: map(exercises, (it) => (it.id === itemId ? next : it)),
        });
    };

    const handleQuestionsSidebarRowsChange = (rows: CompositeQuestionsSidebarRow[]) => {
        if (disabled) {
            return;
        }
        const nextEx = orderItemsByQuestionsSidebarRows(exercises, rows);
        updateData({
            exercises: nextEx,
            questionsSidebarRows: persistQuestionsSidebarRows(rows),
        });
    };

    const handleAddSidebarSection = () => {
        if (disabled) {
            return;
        }
        const sid = generateId();
        const base = resolvedQuestionsSidebarRows(
            safeData.questionsSidebarRows,
            map(exercises, (e) => e.id)
        );
        handleQuestionsSidebarRowsChange([
            {
                kind: 'section',
                section: { id: sid, title: '', description: createEmptyDocument() },
            },
            ...base,
        ]);
        setSelectedSectionId(sid);
        setSelectedId(null);
    };

    const sidebarRows = React.useMemo(
        () => resolvedQuestionsSidebarRows(safeData.questionsSidebarRows, map(exercises, (e) => e.id)),
        [exercises, safeData.questionsSidebarRows]
    );

    const orderingAddOptions = [
        { id: ORDERING_EXERCISE_TYPE_ID, label: t('addExerciseOption', 'Ordering') },
    ];

    const tabs: EditTabDescriptor[] = [
        { id: 'content', label: t('contentTab', 'Content'), icon: FileText, badge: contentSectionCount },
        { id: 'questions', label: t('questionsTab', 'Questions'), icon: ListOrdered, badge: exercises.length },
        ...(configSlot ? [{ id: 'config', label: t('configTab', 'Configuration'), icon: Settings }] : []),
    ];

    const headerMeta = (
        <div className="ordering-content-edit-header space-y-4">
            {editorOptions?.showTitle !== false && (
                <div className="space-y-2">
                    <Label htmlFor="ordering-content-title">{t('titleLabel', 'Title')}</Label>
                    <Input
                        id="ordering-content-title"
                        value={safeData.title || ''}
                        onChange={(e) => updateData({ title: e.target.value })}
                        placeholder={t('titlePlaceholder', 'Enter title')}
                        disabled={disabled}
                        required
                        className="ordering-content-edit-title-input text-lg font-semibold"
                    />
                </div>
            )}
            {editorOptions?.showDescription !== false && (
                <div className="space-y-2">
                    <Label htmlFor="ordering-content-description">
                        {t('descLabel', 'Instructions (Optional)')}
                    </Label>
                    <Textarea
                        id="ordering-content-description"
                        value={safeData.description || ''}
                        onChange={(e) => updateData({ description: e.target.value })}
                        placeholder={t('descPlaceholder', 'Enter instructions...')}
                        disabled={disabled}
                        rows={3}
                        className="ordering-content-edit-description-input"
                    />
                </div>
            )}
        </div>
    );

    const hideHeader =
        editorOptions?.showTitle === false && editorOptions?.showDescription === false;

    return (
        <CompositeEditShell
            prefix="ordering-content-edit"
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(id) => setActiveTab(id as TabValue)}
            configSlot={configSlot}
            className={cn('ordering-content-edit h-full min-h-0', className)}
            renderPanel={(tabId) => {
                if (tabId === 'content') {
                    if (hideHeader) {
                        return (
                            <div className="ordering-content-edit-content ordering-content-edit-lesson-panel flex-1 min-h-0 overflow-y-auto px-1">
                                <PageEdit
                                    value={safeData.lessonContent}
                                    onChange={handleLessonChange}
                                    disabled={disabled}
                                    hideSettings
                                    allowedBlockTypes={PAGE_BLOCK_TYPES}
                                    className="ordering-content-edit-lesson-page-inner"
                                />
                            </div>
                        );
                    }
                    return (
                        <CompositeEditContentTab
                            prefix="ordering-content-edit"
                            meta={headerMeta}
                            page={
                                <PageEdit
                                    value={safeData.lessonContent}
                                    onChange={handleLessonChange}
                                    disabled={disabled}
                                    hideSettings
                                    allowedBlockTypes={PAGE_BLOCK_TYPES}
                                    className="ordering-content-edit-lesson-page-inner"
                                />
                            }
                        />
                    );
                }
                if (tabId === 'questions') {
                    return (
                        <CompositeEditSortableQuestionsLayout
                            prefix="ordering-content-edit"
                            listTitle={t('questionsListTitle', 'Questions')}
                            emptyMessage={t('emptyQuestionsMsg', 'No questions yet. Add an ordering exercise.')}
                            items={exercises}
                            selectedId={selectedId}
                            onSelectId={setSelectedId}
                            selectedSectionId={selectedSectionId}
                            onSelectSectionId={setSelectedSectionId}
                            disabled={disabled}
                            minItemsToKeep={1}
                            sidebarRows={sidebarRows}
                            onSidebarRowsChange={handleQuestionsSidebarRowsChange}
                            onAddSidebarSection={handleAddSidebarSection}
                            addSidebarSectionLabel={t('addSection', 'Add section')}
                            removeSidebarSectionLabel={t('removeSection', 'Remove section')}
                            sectionDetailTitle={t('sectionDetailTitle', 'Question section')}
                            onRemoveItem={removeExercise}
                            itemHasValidationError={(index) =>
                                hasErrorsForExerciseIndex(errors, index)
                            }
                            getItemLabel={(itemId, index) => {
                                const ex = exercises.find((e) => e.id === itemId);
                                if (!ex) return '';
                                return exercisePreview(ex, index, t);
                            }}
                            addButtonLabel={t('addExerciseLabel', 'Add exercise')}
                            addButtonOptions={orderingAddOptions}
                            addButtonClassName="ordering-content-edit-add-exercise"
                            onAddItem={() => addExercise()}
                            detailTitle={t('questionDetailTitle', 'Question')}
                            renderItemDetail={({ itemId, index }) => {
                                const ex = exercises.find((e) => e.id === itemId);
                                if (!ex) {
                                    return null;
                                }
                                const elementErrors = pickErrorsUnderPrefix(
                                    errors,
                                    exerciseDataErrorsPrefix(index)
                                );
                                return (
                                    <OrderingElementEdit
                                        value={ex.data}
                                        onChange={(d: OrderingElementData) =>
                                            updateExercise(ex.id, {
                                                ...ex,
                                                data: d,
                                            })
                                        }
                                        disabled={disabled}
                                        errors={elementErrors}
                                        className="ordering-content-edit-ordering-inner"
                                    />
                                );
                            }}
                            detailContent={null}
                            detailEmptyMessage={t('detailEmptyMessage', 'Select a question, a section, or add one to start.')}
                            richTextTranslate={t}
                        />
                    );
                }
                return null;
            }}
        />
    );
};

export default OrderingContentEdit;
